import React, { useContext, useEffect, useState } from "react";
import { ContentSection, createListTableColumns, ListTable, Stack } from "sprint-common-web";

import { LanguagesDataContext } from "../../../../common/data/lookup-data-providers/LanguagesDataProvider";
import { getLanguageDisplayName } from "../../../../common/types/Language";
import { LanguageSpecificationType } from "../../../common/types/LanguageSpecificationType";
import { MuxSpecificationDashboardContext } from "../context/MuxSpecificationDashboardContext";
import {
    DictionaryLanguageSpecificationDashboardItem,
    ensureIsManzanitaMuxSpecification,
    LanguageSpecificationDashboardItem
} from "../MuxSpecificationDashboardItem";
import { EditLanguageSpecificationButton } from "./buttons/EditLanguageSpecificationButton";

interface ManzanitaMuxSpecificationLanguageMappingsSectionProps {
    height: number;
}

export interface ManzanitaMuxSpecificationLanguageMappingListingItem {
    id: string;
    languageDisplayName: string;
    soundtrackPid: string | undefined;
    subtitlePid: string | undefined;
}

export const ManzanitaMuxSpecificationLanguageMappingsSection: React.VFC<ManzanitaMuxSpecificationLanguageMappingsSectionProps> = ({
    height
}: ManzanitaMuxSpecificationLanguageMappingsSectionProps) => {
    const { languages, isLoading: areLanguagesLoading } = useContext(LanguagesDataContext);
    const { muxSpecificationDashboardItem, isLoading } = useContext(MuxSpecificationDashboardContext);

    ensureIsManzanitaMuxSpecification(muxSpecificationDashboardItem);

    const [items, setItems] = useState<ManzanitaMuxSpecificationLanguageMappingListingItem[]>([]);

    useEffect(() => {
        if (muxSpecificationDashboardItem.languageSpecification) {
            ensureDictionaryLanguageSpecification(muxSpecificationDashboardItem.languageSpecification);

            const specMappingListingItems = getSpecificationMappingListingItems(
                muxSpecificationDashboardItem.languageSpecification
            );

            setItems(specMappingListingItems);
        }
    }, [muxSpecificationDashboardItem, languages.length]);

    const columns = createListTableColumns<ManzanitaMuxSpecificationLanguageMappingListingItem>([
        {
            type: "basic",
            key: "languageDisplayName",
            name: "Language",
            minWidth: 65,
            maxWidth: 175
        },
        {
            type: "basic",
            key: "soundtrackPid",
            name: "Soundtrack PID",
            minWidth: 30,
            maxWidth: 60
        },
        {
            type: "basic",
            key: "subtitlePid",
            name: "Subtitle PID",
            minWidth: 30,
            maxWidth: 60
        },
        {
            type: "buttons",
            key: "actions",
            name: "Actions",
            minWidth: 40,
            maxWidth: 40,
            buttons: [
                {
                    id: "EditLanguageSpecification",
                    render: EditLanguageSpecificationButton
                }
            ]
        }
    ]);

    const getSpecificationMappingListingItems = (
        languageSpecification: DictionaryLanguageSpecificationDashboardItem
    ): ManzanitaMuxSpecificationLanguageMappingListingItem[] => {
        return languages.map((language) => ({
            id: language.id,
            languageDisplayName: getLanguageDisplayName(language),
            soundtrackPid: languageSpecification.soundtrackLanguagePidDictionary[language.id],
            subtitlePid: languageSpecification.subtitleLanguagePidDictionary[language.id]
        }));
    };

    return (
        <ContentSection
            header="Languages Specification"
            style={{ height }}
            isLoading={isLoading || areLanguagesLoading}
        >
            <Stack styles={{ root: { overflowX: "hidden", overflowY: "auto", height: height - 100 } }}>
                <ListTable columns={columns} items={items} isLoading={isLoading} />
            </Stack>
        </ContentSection>
    );
};

export const ensureDictionaryLanguageSpecification: (
    languageSpecification: LanguageSpecificationDashboardItem
) => asserts languageSpecification is DictionaryLanguageSpecificationDashboardItem = (
    languageSpecification: LanguageSpecificationDashboardItem
) => {
    if (languageSpecification.type !== LanguageSpecificationType.Dictionary) {
        throw new Error("LanguageSpecificationType should be Dictionary");
    }
};
